import React, { useState } from "react";
import { useSharedValue } from "../components/Constants/constants";
import {
  getUserIdFromLocalStorage,
  getExcelFile,
  getFileBytes,
  getFileSasUrl,
} from "../API_Endpoints/APIEndpoints";
import { BASE_URL, businessunitName, pythonBaseUrl } from "../App_Settings/app_settings";
import { CircularProgress } from "@mui/material";
import downloadFileIcon from "../assets/new icons/reports/download file.svg";
import excelSvgIcon from "../assets/new icons/reports/excel.svg";
import pdfSvgIcon from "../assets/new icons/reports/pdf.svg";

export const DownloadExcelFile = ({
  fileName,
  title = "Excel",
  businessunit,
  userId,
  organizationId,
  iconSrc,
  iconFilter,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async (e) => {
    e.preventDefault();

    if (!fileName) {
      alert("Error: No file identifier provided for download");
      return;
    }

    const confirmation = window.confirm(
      "\u26A0 Important Information:\nYou are downloading Excel to your local storage. Please delete this file later.\n\nClick 'OK' to proceed with download."
    );

    if (confirmation) {
      setIsDownloading(true);
      try {
        await getExcelFile(organizationId, userId, businessunit, fileName);
      } catch (error) {
        console.error("Error downloading Excel file:", error);
        alert("Failed to download Excel file. Please try again.");
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownloadClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 28,
        height: 28,
        padding: 0,
        border: "none",
        background: "transparent",
        cursor: "pointer",
      }}
    >
      {isDownloading ? (
        <CircularProgress size={20} thickness={6} style={{ color: "var(--loader-color)" }} />
      ) : (
        <img
          title="Download Excel"
          src={iconSrc || excelSvgIcon}
          alt="Excel"
          style={{
            height: 22,
            width: 22,
            verticalAlign: "middle",
            filter: iconFilter || "hue-rotate(175deg) saturate(1.25)",
          }}
        />
      )}
    </button>
  );
};

export const DownloadOriginalFile = ({
  fileImportId,
  fileName,
  userId,
  organizationId,
  businessunit,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!fileImportId || !fileName) return;
    setIsDownloading(true);
    try {
      const extension = fileName.split(".").pop()?.toLowerCase() || "";
      const type = "uploadfiles";
      try {
        const sasUrl = await getFileSasUrl(
          organizationId,
          userId,
          fileImportId,
          fileName,
          extension,
          type
        );
        if (sasUrl) {
          const response = await fetch(sasUrl);
          if (!response.ok) {
            throw new Error(`Failed to download original file: ${response.status}`);
          }

          const fileBlob = await response.blob();
          const url = URL.createObjectURL(fileBlob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(url);
          return;
        }
      } catch {
        // Fall back to the authenticated byte endpoint when SAS is unavailable.
      }

      const data = await getFileBytes(
        organizationId,
        userId,
        fileImportId,
        fileName,
        extension,
        type
      );
      const url = URL.createObjectURL(new Blob([data.fileBytes], { type: data.mimeType }));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading original file:", error);
      alert("Failed to download the original file. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const extension = fileName?.split(".").pop()?.toLowerCase();
  const isExcel = ["xlsx", "xls", "xltx"].includes(extension);
  const isWord = ["doc", "docx"].includes(extension);
  const iconTitle = `Download ${isExcel ? "Excel" : isWord ? "Word" : "PDF"}`;

  return (
    <span title={iconTitle}>
      {isDownloading ? (
        <CircularProgress size={20} thickness={6} style={{ color: "var(--loader-color)" }} />
      ) : isExcel ? (
        <ExcelIcon onclick={handleDownload} title={iconTitle} />
      ) : isWord ? (
        <WordIcon onclick={handleDownload} />
      ) : (
        <img
          src={pdfSvgIcon}
          alt="PDF"
          title={iconTitle}
          onClick={handleDownload}
          style={{ width: 22, height: 22, marginRight: 6, cursor: "pointer" }}
        />
      )}
    </span>
  );
};

export const DownloadXMLFile = ({
  fileName,
  fileImportId,
  businessunit,
  businessUnitAdminUrl,
  title = "XML",
  userId,
  organizationId,
  iconSrc,
  iconFilter,
  containerSize = 25,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async (e) => {
    e.preventDefault();

    if (!fileImportId) {
      alert("Error: No file identifier provided for download");
      return;
    }

    const confirmation = window.confirm(
      "\u26A0 Important Information:\nYou are downloading XML to your local storage. Please delete this file later from your local storage.\n\nClick 'OK' to proceed with download."
    );

    if (confirmation) {
      setIsDownloading(true);
      try {
        const formData = new FormData();
        formData.append("filename", fileImportId);
        formData.append("organization_id", organizationId);
        formData.append("user_id", userId);
        formData.append("business_unit", businessunit || "Demo");
        formData.append("business_unit_admin_url", businessUnitAdminUrl || "");

        const response = await fetch(`${pythonBaseUrl}/Excel/DownloadXmlData`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
            accept: "*/*",
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Failed to download XML: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${fileName || fileImportId}.xml`;
        a.click();
        window.URL.revokeObjectURL(url);

        const downloadTime = new Date();
        localStorage.setItem("downloadTime", downloadTime);
      } catch (error) {
        console.error("Error downloading XML:", error);
        alert("Failed to download XML. Please try again.");
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <span
      onClick={handleDownloadClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: containerSize,
        height: containerSize,
        cursor: "pointer",
      }}
      title="Download XML"
    >
      {isDownloading ? (
        <CircularProgress size={20} thickness={6} style={{ color: "var(--loader-color)" }} />
      ) : (
        <img
          src={iconSrc || downloadFileIcon}
          alt="XML"
          style={{
            width: 22,
            height: 22,
            filter: iconFilter || "var(--icon-download-filter)",
          }}
        />
      )}
    </span>
  );
};

export const ExcelIcon = ({ onclick, title = "Download Audit Report", audit = false }) => {
  return (
    <img
      title={title}
      src={excelSvgIcon}
      alt="Excel"
      onClick={onclick}
      style={{
        height: 22,
        width: 22,
        verticalAlign: "middle",
        marginRight: 6,
        cursor: "pointer",
        ...(audit ? { filter: "hue-rotate(285deg) saturate(1.25)" } : {}),
      }}
    />
  );
};

export const WordIcon = ({ onclick }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#2b579a"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      onClick={onclick}
      style={{
        width: 24,
        height: 24,
        verticalAlign: "middle",
        marginRight: 6,
        cursor: "pointer",
      }}
      title="View Word Document"
    >
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  );
};

export const ViewPDf = ({ fileImportId, fileName, userId, organizationId }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownloadPdf = async () => {
    try {
      if (!fileName) {
        alert("Cannot download: missing original file name.");
        return;
      }
      setIsLoading(true);
      const extension = (fileName.split(".").pop() || "pdf").toLowerCase();
      const type = "uploadfiles";

      // Fast path: open SAS URL directly — browser streams from Azure, no backend proxy
      try {
        const sasUrl = await getFileSasUrl(
          organizationId,
          userId,
          fileImportId,
          fileName,
          extension,
          type
        );
        if (sasUrl) {
          window.open(sasUrl, "_blank");
          return;
        }
      } catch {
        // SAS URL unavailable — fall through to byte download
      }

      // Fallback: download full bytes through backend
      const data = await getFileBytes(
        organizationId,
        userId,
        fileImportId,
        fileName,
        extension,
        type
      );
      const blob = new Blob([data.fileBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error("Failed to view PDF:", error);
      alert("Failed to open PDF. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div onClick={isLoading ? undefined : handleDownloadPdf}>
      {" "}
      {isLoading ? (
        <CircularProgress
          size={22}
          style={{ color: "var(--loader-color)", verticalAlign: "middle" }}
        />
      ) : (
        <img
          title="View PDF"
          src={pdfSvgIcon}
          alt="PDF"
          style={{
            height: 22,
            width: 22,
            verticalAlign: "middle",
            cursor: "pointer",
          }}
        />
      )}
    </div>
  );
};
export const formatDate = (value) => {
  // Handle null, undefined, or empty values
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn("formatDate received invalid date:", value);
    return "-";
  }

  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.toLocaleString("en-US", { day: "2-digit" });
  const year = date.toLocaleString("en-US", { year: "numeric" });
  const hour24 = date.getHours();

  const minute = date.getMinutes();

  const amPm = hour24 < 12 ? "am" : "pm";

  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const formattedHour = hour12 < 10 ? `0${hour12}` : hour12;
  const formattedMinute = minute < 10 ? `0${minute}` : minute;

  return `${month} ${day}, ${year} ${formattedHour}:${formattedMinute} ${amPm}`;
};

export const useNotifications = (newNotification) => {
  const { notifications, handleNotificationList } = useSharedValue();

  // const addNotification = (newNotification) => {
  const updatedNotifications = [
    ...(Array.isArray(notifications) ? notifications : []),
    newNotification,
  ];
  handleNotificationList(updatedNotifications);
  // };
};

export const checkIsSuperAdmin = async () => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;

    const response = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
    });

    if (response.ok) {
      const user = await response.json();
      return user?.isSuperAdmin || false;
    }
    return false;
  } catch (error) {
    console.error("Error checking super admin status:", error);
    return false;
  }
};

// Function to check if the user is an admin
export const checkIsAdmin = async () => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;
    const response = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
    });
    if (response.ok) {
      const user = await response.json();
      // Assuming userRoles is an array of role objects with a name property
      return user?.userRoles?.some((role) => role.name === "Admin") || false;
    }
    return false;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
};

// Reads role claims from the signed JWT so client-side scoping cannot be bypassed
// by editing plain fields in localStorage. Returns an array of role name strings.
const _parseJwtPayload = (jwt) => {
  try {
    const base64Url = jwt.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const getUserRolesFromToken = () => {
  const { token } = getUserIdFromLocalStorage() || {};
  if (!token) return { roles: [], isSuperAdmin: false };
  const payload = _parseJwtPayload(token);
  if (!payload) return { roles: [], isSuperAdmin: false };
  const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
  const raw = payload[ROLE_CLAIM] || payload["role"] || [];
  const roles = Array.isArray(raw) ? raw : [raw];
  const isSuperAdmin = String(payload["IsSuperAdmin"] ?? "false").toLowerCase() === "true";
  return { roles, isSuperAdmin };
};

// True when the user is BOTH Maker and Checker without Admin/SuperAdmin — restrict
// them to files they uploaded themselves to keep the Maker/Checker split intact.
const isMakerCheckerOnly = () => {
  const { roles, isSuperAdmin } = getUserRolesFromToken();
  if (isSuperAdmin || roles.includes("Admin")) return false;
  return roles.includes("Maker") && roles.includes("Checker");
};

export const getAllFilesList = async (
  history,
  defaultOrganization,
  defaultBusinessunit,
  defaultUser,
  status = null,
  businessUnitId = null
) => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;
    const token = getUserIdFromLocalStorage().token;

    // Build URL with optional status parameter
    const baseUrl = `${BASE_URL}/FileData/GetAllFiles/${userId}/${organizationId}`;
    const params = new URLSearchParams();
    if (status !== null) params.set("status", status);
    if (businessUnitId) params.set("businessUnitId", businessUnitId);
    const queryString = params.toString();
    const url = queryString ? `${baseUrl}/?${queryString}` : baseUrl;

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 401) {
      handleLogout(history);
      return [];
    }
    if (response.ok) {
      const data = await response.json();
      // Maker+Checker (non-Admin): can only see files they uploaded themselves.
      const restrictToOwn = isMakerCheckerOnly();
      const scopedData = restrictToOwn ? data.filter((row) => row.userID === userId) : data;
      if (defaultOrganization && defaultOrganization.name !== "") {
        const organization = await getAllFilesListWithOrganization(
          history,
          defaultOrganization,
          status,
          businessUnitId
        );
        const scopedOrg = restrictToOwn
          ? organization.filter((row) => row.userID === userId)
          : organization;
        if (defaultBusinessunit && defaultBusinessunit !== `All - ${businessunitName}`) {
          return businessUnitId
            ? scopedOrg.filter(
                (row) =>
                  String(row.businessUnitID || row.businessUnitId || "") === String(businessUnitId)
              )
            : scopedOrg.filter((row) => row.businessUnit === defaultBusinessunit);
        } else {
          return scopedOrg;
        }
      } else if (defaultUser && defaultUser !== "") {
        if (defaultBusinessunit && defaultBusinessunit !== `All - ${businessunitName}`) {
          return scopedData.filter(
            (row) => row.userID === defaultUser && row.businessUnit === defaultBusinessunit
          );
        } else {
          return scopedData.filter((row) => row.userID === defaultUser.id);
        }
      } else {
        if (defaultBusinessunit && defaultBusinessunit !== `All - ${businessunitName}`) {
          return scopedData.filter((row) => row.businessUnit === defaultBusinessunit);
        } else {
          return scopedData;
        }
      }
    }
    return [];
  } catch (error) {
    console.error("Error fetching all files list:", error);
    return [];
  }
};
export const getAllFilesListWithOrganization = async (
  history,
  defaultOrganization,
  status = null,
  businessUnitId = null
) => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;
    const token = getUserIdFromLocalStorage().token;

    // Build URL with optional status parameter
    const baseUrl = `${BASE_URL}/FileData/GetAllFiles/${userId}/${defaultOrganization.id}`;
    const params = new URLSearchParams();
    if (status !== null) params.set("status", status);
    if (businessUnitId) params.set("businessUnitId", businessUnitId);
    const queryString = params.toString();
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 401) {
      handleLogout(history);
      return [];
    }
    if (response.ok) {
      return await response.json();
    }
    return [];
  } catch (error) {
    console.error("Error fetching all files list:", error);
    return [];
  }
};

export const handleLogout = async (history) => {
  // Terminate current session on server BEFORE clearing local storage
  const sessionToken = localStorage.getItem("sessionToken");
  const tokenData = localStorage.getItem("token");
  try {
    if (sessionToken && tokenData) {
      const userData = JSON.parse(tokenData);
      const response = await fetch(`${BASE_URL}/api/SessionManagement/logout-all`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "X-Session-Token": sessionToken,
        },
      });
      if (!response.ok) {
        console.warn("Server-side session termination returned:", response.status);
      }
    }
  } catch (error) {
    console.error("Session logout error:", error);
  } finally {
    // Always clear client storage and redirect, even if server call fails.
    localStorage.clear();
    sessionStorage.clear();
    _cachedAssignedBUIdsByUser.clear();
    history.replace("/");
  }
};

/**
 * Fetch all business units for an organization
 * @param {string} organizationId - Optional organization ID. Uses current user's org if not provided
 * @returns {Promise<Array>} - Array of business units or empty array on error
 */
export const getBusinessUnits = async (organizationId = null) => {
  try {
    const orgId = organizationId || getUserIdFromLocalStorage().organizationId;
    const token = getUserIdFromLocalStorage().token;
    const sessionToken = localStorage.getItem("sessionToken");

    const response = await fetch(`${BASE_URL}/BusinessUnits/${orgId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(sessionToken && { "X-Session-Token": sessionToken }),
      },
    });

    if (response.ok) {
      return await response.json();
    }
    return [];
  } catch (error) {
    console.error("Error fetching business units:", error);
    return [];
  }
};

/**
 * Fetch the current user's assigned business unit IDs from the user API.
 * Cached in-memory per session so it's only fetched once.
 * @returns {Promise<Set<string>>} - Set of business unit IDs assigned to the user
 */
let _cachedAssignedBUIdsByUser = new Map();
export const getUserAssignedBusinessUnitIds = async (
  targetUserId = null,
  targetOrganizationId = null
) => {
  try {
    const currentUser = getUserIdFromLocalStorage();
    const userId = targetUserId || currentUser.userId;
    const organizationId = targetOrganizationId || currentUser.organizationId;
    const token = currentUser.token;
    if (!userId || !organizationId || !token) return new Set();
    const cacheKey = `${organizationId}:${userId}`;
    if (_cachedAssignedBUIdsByUser.has(cacheKey)) {
      return _cachedAssignedBUIdsByUser.get(cacheKey);
    }
    const sessionToken = localStorage.getItem("sessionToken");
    const response = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(sessionToken && { "X-Session-Token": sessionToken }),
      },
    });
    if (!response.ok) return new Set();
    const user = await response.json();
    const ids = Array.isArray(user.businessUnits)
      ? user.businessUnits.map((bu) => bu.id || bu.businessUnitId).filter(Boolean)
      : [];
    const assignedIds = new Set(ids);
    _cachedAssignedBUIdsByUser.set(cacheKey, assignedIds);
    return assignedIds;
  } catch (error) {
    console.error("Error fetching user's assigned business units:", error);
    return new Set();
  }
};

/**
 * Format date to MM/DD/YYYY format for XML generation
 * Handles multiple input formats and avoids timezone conversion issues
 * @param {string} dateStr - Input date string in various formats
 * @returns {string} - Date in MM/DD/YYYY format or original string if parsing fails
 */
export const formatDateForXML = (dateStr) => {
  if (!dateStr) return "";

  // Try to parse MM/DD/YYYY or MM/DD/YY format first (US date format)
  const usDatePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;
  const usDateMatch = dateStr.match(usDatePattern);

  if (usDateMatch) {
    const [, month, day, year] = usDateMatch;
    // Convert 2-digit year to 4-digit year
    const fullYear = year.length === 2 ? `20${year}` : year;
    // Pad month and day with leading zeros and return in MM/DD/YYYY format
    const formattedDate = `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${fullYear}`;

    return formattedDate;
  }

  // Try to parse YYYY-MM-DD format and convert to MM/DD/YYYY
  const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
  const isoDateMatch = dateStr.match(isoDatePattern);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    const formattedDate = `${month}/${day}/${year}`;

    return formattedDate;
  }

  // Try to format the date if it's in a standard format
  // Use local date methods to avoid timezone conversion issues
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    // Use local date methods to preserve the date as entered
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const formattedDate = `${month}/${day}/${year}`;

    return formattedDate;
  } catch (e) {
    return dateStr;
  }
};
// Normalizes multiple date formats to MM/DD/YYYY; returns empty string if unrecognised
export const normalizeDateToSlash = (dateStr) => {
  if (!dateStr) return dateStr;

  const MONTHS = {
    january: "01",
    february: "02",
    march: "03",
    april: "04",
    may: "05",
    june: "06",
    july: "07",
    august: "08",
    september: "09",
    october: "10",
    november: "11",
    december: "12",
    jan: "01",
    feb: "02",
    mar: "03",
    apr: "04",
    jun: "06",
    jul: "07",
    aug: "08",
    sep: "09",
    oct: "10",
    nov: "11",
    dec: "12",
  };

  // MM/DD/YYYY or MM/DD/YY
  const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    const fullYear = year.length === 2 ? `20${year}` : year;
    return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${fullYear}`;
  }

  // MM-DD-YYYY or MM-DD-YY
  const dashMatch = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{2,4})$/);
  if (dashMatch) {
    const [, month, day, year] = dashMatch;
    const fullYear = year.length === 2 ? `20${year}` : year;
    return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${fullYear}`;
  }

  // "31st May 2025" / "31 May 2025" — day (+ ordinal) month-name year
  const dayFirstMatch = dateStr.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)\s+(\d{4})$/);
  if (dayFirstMatch) {
    const [, day, monthName, year] = dayFirstMatch;
    const month = MONTHS[monthName.toLowerCase()];
    if (month) return `${month}/${day.padStart(2, "0")}/${year}`;
  }

  // "May 31 2025" / "May 31, 2025" — month-name day year
  const monthFirstMatch = dateStr.match(/^([A-Za-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/);
  if (monthFirstMatch) {
    const [, monthName, day, year] = monthFirstMatch;
    const month = MONTHS[monthName.toLowerCase()];
    if (month) return `${month}/${day.padStart(2, "0")}/${year}`;
  }

  return "";
};
/**
 * Generates and downloads the audit Excel report client-side using SheetJS.
 * Shared between approvalReview.js and extractionApproval.js.
 *
 * @param {Array} extractedValidatedata - The audit rows array
 * @param {Function} setIsLoading - Loading state setter
 */
export const downloadAuditExcel = async (extractedValidatedata, setIsLoading) => {
  try {
    setIsLoading(true);

    // Map to the same columns shown in the Audit table (matches auditColumnNames in list.js)
    const formatted = (extractedValidatedata || []).map((item) => ({
      "Data Type": item.dataType || "",
      Action: item.action || "",
      "Unique Identifier": item.uid || item.UID || "",
      "Field Name": item.fieldName || item.FieldName || "",
      "Old Value": item.oldValue || item.OldValue || "",
      "New Value": item.newValue || item.NewValue || "",
      "User Name": item.userName || item.UserName || "",
      "Modified Date":
        item.modifiedDate || item.ModifiedDate
          ? formatDate(item.modifiedDate || item.ModifiedDate)
          : "",
    }));

    const now = new Date();
    const dateTimeString = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const fileName = `user_audit_report_${dateTimeString}.xlsx`;

    const XLSX = await import("xlsx");
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Audit");
    const xlsxBytes = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([xlsxBytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const downloadUrl = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = downloadUrl;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (err) {
    console.error("Error downloading audit Excel:", err);
    alert("Failed to download audit Excel.");
  } finally {
    setIsLoading(false);
  }
};
